import React, { useState } from 'react';
import { ChevronRight, Users, Mail, TrendingUp, Star, CheckCircle, ArrowRight } from 'lucide-react';

interface QuizData {
  socialFollowers: string;
  emailContacts: string;
  payingCustomers: string;
  monthlyRevenue: string;
}

interface AuthorityResult {
  score: number;
  level: string;
  color: string;
  description: string;
  visibilityScore: number;
  trustScore: number;
  potential: string;
}



const AuthorityQuotientTest: React.FC = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [quizData, setQuizData] = useState<QuizData>({
    socialFollowers: '',
    emailContacts: '',
    payingCustomers: '',
    monthlyRevenue: ''
  });
  const [result, setResult] = useState<AuthorityResult | null>(null);
  const [leadData, setLeadData] = useState({ name: '', email: '' });
  const [submitted, setSubmitted] = useState(false);

  const rangeOptions = {
    socialFollowers: [
      { value: '0-1000', label: '0 - 1,000', numericValue: 500 },
      { value: '1000-10000', label: '1,000 - 10,000', numericValue: 5500 },
      { value: '10000-100000', label: '10,000 - 100,000', numericValue: 55000 },
      { value: '100000+', label: '100,000+', numericValue: 200000 }
    ],
    emailContacts: [
      { value: '0-1000', label: '0 - 1,000', numericValue: 500 },
      { value: '1000-10000', label: '1,000 - 10,000', numericValue: 5500 },
      { value: '10000-100000', label: '10,000 - 100,000', numericValue: 55000 },
      { value: '100000+', label: '100,000+', numericValue: 200000 }
    ],
    payingCustomers: [
      { value: '0', label: '0', numericValue: 0 },
      { value: '1-50', label: '1 - 50', numericValue: 25 },
      { value: '51-250', label: '51 - 250', numericValue: 150 },
      { value: '251-1000', label: '251 - 1,000', numericValue: 625 },
      { value: '1001-5000', label: '1,001 - 5,000', numericValue: 3000 },
      { value: '5001-10000', label: '5,001 - 10,000', numericValue: 7500 },
      { value: '10000+', label: '10,000+', numericValue: 15000 }
    ],
    monthlyRevenue: [
      { value: '0-100000', label: '₦0 - ₦100,000', numericValue: 50000 },
      { value: '100001-500000', label: '₦100,001 - ₦500,000', numericValue: 300000 },
      { value: '500001-1000000', label: '₦500,001 - ₦1,000,000', numericValue: 750000 },
      { value: '1000001-2500000', label: '₦1,000,001 - ₦2,500,000', numericValue: 1750000 },
      { value: '2500001-5000000', label: '₦2,500,001 - ₦5,000,000', numericValue: 3750000 },
      { value: '5000001-10000000', label: '₦5,000,001 - ₦10,000,000', numericValue: 7500000 },
      { value: '10000001-25000000', label: '₦10,000,001 - ₦25,000,000', numericValue: 17500000 },
      { value: '25000000+', label: '₦25,000,000+', numericValue: 35000000 }
    ]
  };

  const questions = [
    {
      title: "Social Media Presence",
      subtitle: "How many total followers do you have across all social media platforms?",
      icon: <Users className="w-6 h-6" />,
      field: 'socialFollowers' as keyof QuizData,
      options: rangeOptions.socialFollowers
    },
    {
      title: "Email List Size",
      subtitle: "How many contacts do you have in your email list?",
      icon: <Mail className="w-6 h-6" />,
      field: 'emailContacts' as keyof QuizData,
      options: rangeOptions.emailContacts
    },
    {
      title: "Paying Customers",
      subtitle: "How many of your followers/contacts are paying customers?",
      icon: <CheckCircle className="w-6 h-6" />,
      field: 'payingCustomers' as keyof QuizData,
      options: rangeOptions.payingCustomers
    },
    {
      title: "Monthly Revenue",
      subtitle: "What's your approximate monthly revenue from your business?",
      icon: <TrendingUp className="w-6 h-6" />,
      field: 'monthlyRevenue' as keyof QuizData,
      options: rangeOptions.monthlyRevenue
    }
  ];

  const getNumericValue = (field: keyof QuizData, value: string): number => {
    const options = rangeOptions[field];
    const option = options.find(opt => opt.value === value);
    return option ? option.numericValue : 0;
  };

  const calculateAuthority = (): AuthorityResult => {
    const socialFollowersNum = getNumericValue('socialFollowers', quizData.socialFollowers);
    const emailContactsNum = getNumericValue('emailContacts', quizData.emailContacts);
    const payingCustomersNum = getNumericValue('payingCustomers', quizData.payingCustomers);
    const monthlyRevenueNum = getNumericValue('monthlyRevenue', quizData.monthlyRevenue);
    
    const totalAudience = socialFollowersNum + emailContactsNum;
    const conversionRate = totalAudience > 0 ? (payingCustomersNum / totalAudience) * 100 : 0;
    
    // Visibility Score (40% of total) - adjusted for higher ranges
    let visibilityScore = 0;
    if (totalAudience >= 200000) visibilityScore = 40;
    else if (totalAudience >= 100000) visibilityScore = 35;
    else if (totalAudience >= 50000) visibilityScore = 30;
    else if (totalAudience >= 20000) visibilityScore = 25;
    else if (totalAudience >= 10000) visibilityScore = 20;
    else if (totalAudience >= 5000) visibilityScore = 15;
    else if (totalAudience >= 1000) visibilityScore = 10;
    else visibilityScore = 5;

    // Trust Score (60% of total) - adjusted for Naira values
    let trustScore = 0;
    const revenueMultiplier = monthlyRevenueNum / 100000; // Revenue in hundreds of thousands of Naira
    
    if (conversionRate >= 15) trustScore = 35;
    else if (conversionRate >= 10) trustScore = 30;
    else if (conversionRate >= 5) trustScore = 25;
    else if (conversionRate >= 2) trustScore = 20;
    else if (conversionRate >= 1) trustScore = 15;
    else if (conversionRate >= 0.5) trustScore = 10;
    else trustScore = 5;

    // Revenue bonus (up to 25 points) - adjusted for Naira scale
    const revenueBonus = Math.min(25, revenueMultiplier * 0.3);
    trustScore += revenueBonus;
    trustScore = Math.min(60, trustScore); // Cap at 60

    const totalScore = visibilityScore + trustScore;

    let level = '';
    let color = '';
    let description = '';
    let potential = '';

    if (totalScore >= 80) {
      level = 'Authority Expert';
      color = 'text-purple-600';
      description = 'You have exceptional authority in your field with strong visibility and high trust.';
      potential = 'Focus on scaling your systems and potentially expanding into new markets or niches.';
    } else if (totalScore >= 60) {
      level = 'Rising Authority';
      color = 'text-blue-600';
      description = 'You have solid authority with good visibility and growing trust among your audience.';
      potential = 'You\'re well-positioned to 10x your authority by optimizing conversion and expanding reach.';
    } else if (totalScore >= 40) {
      level = 'Building Authority';
      color = 'text-green-600';
      description = 'You\'re on the right track with decent visibility, but trust conversion needs improvement.';
      potential = 'Huge potential! Focus on lead magnets and nurturing sequences to convert your audience.';
    } else if (totalScore >= 20) {
      level = 'Emerging Authority';
      color = 'text-yellow-600';
      description = 'You have some foundation but need to work on both visibility and trust building.';
      potential = 'Massive growth opportunity! Start with effective lead magnets and audience building.';
    } else {
      level = 'Starting Journey';
      color = 'text-red-600';
      description = 'You\'re at the beginning of your authority journey. Time to build your foundation!';
      potential = 'Unlimited potential ahead! Focus on creating valuable content and growing your audience.';
    }

    return {
      score: totalScore,
      level,
      color,
      description,
      visibilityScore,
      trustScore,
      potential
    };
  };

  const handleRangeSelect = (field: keyof QuizData, value: string) => {
    setQuizData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const nextStep = () => {
    if (currentStep < questions.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      const authorityResult = calculateAuthority();
      setResult(authorityResult);
      setCurrentStep(questions.length);
    }
  };

  const isCurrentStepComplete = () => {
    if (currentStep >= questions.length) return true;
    const currentField = questions[currentStep].field;
    return quizData[currentField] !== '';
  };

  const submitLead = () => {
    if (leadData.name && leadData.email) {
      setSubmitted(true);
    }
  };

  const restartQuiz = () => {
    setCurrentStep(0);
    setQuizData({
      socialFollowers: '',
      emailContacts: '',
      payingCustomers: '',
      monthlyRevenue: ''
    });
    setResult(null);
    setLeadData({ name: '', email: '' });
    setSubmitted(false);
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Thank You, {leadData.name}!</h2>
          <p className="text-gray-600 mb-6">
            Your detailed Authority Level recommendations have been sent to {leadData.email}. 
            Check your inbox for a comprehensive guide to 10x your authority!
          </p>
          <button
            onClick={restartQuiz}
            className="bg-purple-600 text-white px-6 py-3 rounded-lg hover:bg-purple-700 transition-colors"
          >
            Take Quiz Again
          </button>
        </div>
      </div>
    );
  }

  if (result) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-2xl w-full">
          <div className="text-center mb-8">
            <div className="w-20 h-20 bg-gradient-to-r from-purple-600 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <Star className="w-10 h-10 text-white" />
            </div>
            <h2 className="text-3xl font-bold text-gray-800 mb-2">Your Authority Quotient</h2>
            <div className="text-6xl font-bold mb-2" style={{ color: result.color.replace('text-', '') }}>
              {result.score}
            </div>
            <div className={`text-xl font-semibold ${result.color} mb-4`}>
              {result.level}
            </div>
            <p className="text-gray-600 mb-6">{result.description}</p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-blue-50 rounded-lg p-4">
              <h3 className="font-semibold text-blue-800 mb-2 flex items-center">
                <Users className="w-5 h-5 mr-2" />
                Visibility Score
              </h3>
              <div className="text-2xl font-bold text-blue-600">{result.visibilityScore}/40</div>
              <p className="text-sm text-blue-700">Based on your audience size</p>
            </div>
            <div className="bg-green-50 rounded-lg p-4">
              <h3 className="font-semibold text-green-800 mb-2 flex items-center">
                <CheckCircle className="w-5 h-5 mr-2" />
                Trust Score
              </h3>
              <div className="text-2xl font-bold text-green-600">{result.trustScore}/60</div>
              <p className="text-sm text-green-700">Based on conversions & revenue</p>
            </div>
          </div>

          <div className="bg-gradient-to-r from-purple-100 to-blue-100 rounded-lg p-6 mb-8">
            <h3 className="font-semibold text-purple-800 mb-2 flex items-center">
              <TrendingUp className="w-5 h-5 mr-2" />
              Your Authority Potential
            </h3>
            <p className="text-purple-700">{result.potential}</p>
          </div>

          <div className="border-t pt-6">
            <h3 className="text-xl font-semibold text-gray-800 mb-4">
              Get Your Complete 10x Authority Action Plan
            </h3>
            <p className="text-gray-600 mb-6">
              Enter your details below to receive a personalized roadmap with specific strategies to dramatically increase your authority level.
            </p>
            
            <div className="space-y-4">
              <input
                type="text"
                placeholder="Your Name"
                value={leadData.name}
                onChange={(e) => setLeadData(prev => ({ ...prev, name: e.target.value }))}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
              <input
                type="email"
                placeholder="Your Email Address"
                value={leadData.email}
                onChange={(e) => setLeadData(prev => ({ ...prev, email: e.target.value }))}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
              <button
                onClick={submitLead}
                disabled={!leadData.name || !leadData.email}
                className="w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white py-3 px-6 rounded-lg hover:from-purple-700 hover:to-blue-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                Get My 10x Authority Action Plan
                <ArrowRight className="w-5 h-5 ml-2" />
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 flex items-center justify-center p-4">
      {currentStep < questions.length ? (
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-lg w-full">
          <div className="mb-8">
            <div className="flex justify-between items-center mb-4">
              <span className="text-sm font-medium text-purple-600">
                Question {currentStep + 1} of {questions.length}
              </span>
              <div className="flex space-x-1">
                {questions.map((_, index) => (
                  <div
                    key={index}
                    className={`w-2 h-2 rounded-full ${
                      index <= currentStep ? 'bg-purple-600' : 'bg-gray-300'
                    }`}
                  />
                ))}
              </div>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2 mb-6">
              <div
                className="bg-gradient-to-r from-purple-600 to-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${((currentStep + 1) / questions.length) * 100}%` }}
              />
            </div>
          </div>

          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
              {questions[currentStep].icon}
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">
              {questions[currentStep].title}
            </h2>
            <p className="text-gray-600">
              {questions[currentStep].subtitle}
            </p>
          </div>

          <div className="space-y-4">
            {questions[currentStep].options.map((option) => (
              <button
                key={option.value}
                onClick={() => handleRangeSelect(questions[currentStep].field, option.value)}
                className={`w-full p-4 text-left border-2 rounded-lg transition-all duration-200 hover:border-purple-300 ${
                  quizData[questions[currentStep].field] === option.value
                    ? 'border-purple-600 bg-purple-50 text-purple-800'
                    : 'border-gray-200 hover:bg-gray-50'
                }`}
              >
                <div className="font-medium">{option.label}</div>
              </button>
            ))}
            
            <button
              onClick={nextStep}
              disabled={!isCurrentStepComplete()}
              className="w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white py-3 px-6 rounded-lg hover:from-purple-700 hover:to-blue-700 transition-all duration-200 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed mt-6"
            >
              {currentStep === questions.length - 1 ? 'Calculate My Authority' : 'Next Question'}
              <ChevronRight className="w-5 h-5 ml-2" />
            </button>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-lg w-full text-center">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">
            The Authority Quotient Test
          </h2>
          <p className="text-gray-600 mb-8">
            Discover your authority level in your field and get personalized recommendations to 10x your influence.
          </p>
          <button
            onClick={() => setCurrentStep(0)}
            className="bg-gradient-to-r from-purple-600 to-blue-600 text-white py-3 px-8 rounded-lg hover:from-purple-700 hover:to-blue-700 transition-all duration-200"
          >
            Start Assessment
          </button>
        </div>
      )}
    </div>
  );
};

export default AuthorityQuotientTest;